import { FC, useEffect, useRef, useState } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { fileToBase64 } from '@eneco/common/file';
import logger from '@eneco/common/log';
import { toURL } from '@eneco/common/path';
import { stripAccolades } from '@eneco/common/string';
import { Recaptcha, RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { RecaptchaForm } from '@eneco/components/RecaptchaForm/RecaptchaForm';
import RichText from '@eneco/components/RichText/RichText';
import { useFormsPublicV2PostFormV2 } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRouter } from '@eneco/next';
import { Button, FilePicker, Heading, NotificationBox, Stack, Text } from '@eneco/sparky';
import type { RequestModels_Forms_V2_FormsRequestModelV2 } from '@monorepo-types/dc';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import type { FormErrorType } from './generic-form/types';
import { ContactInputs } from './heat-damage-form/ContactInputs';
import { DamageInputs } from './heat-damage-form/DamageInputs';
import { generateErrorType } from './heat-damage-form/error';
import { getSchema } from './heat-damage-form/schema';
import type { FormValues } from './heat-damage-form/types';
import type {
  HeatDamageFormContent,
  HeatDamageFormFields,
  HeatDamageNotification,
} from './heat-damage-form-content.types';

const FIELD_ROLES: (keyof HeatDamageFormFields)[] = [
  'isHomeOwnerFormField',
  'salutationFormField',
  'descriptionFormField',
  'insurerFormField',
  'estimatedDamageFormField',
  'uploadFilesFormField',
  'firstNameFormField',
  'surnamePrepositionFormField',
  'surnameFormField',
  'streetNameFormField',
  'houseNumberFormField',
  'postalCodeFormField',
  'cityFormField',
  'phoneNumberFormField',
  'emailFormField',
  'customerIdFormField',
  'remarksFormField',
];

// On Sitecore `formFields` and `notifications` are also the template sections themselves (objects),
// next to the flattened descriptors; only Sanity's role-keyed lists are arrays.
const findByRole = <T extends { role?: string }>(entries: T[] | object | undefined, role: string): T | undefined =>
  Array.isArray(entries) ? (entries as T[]).find(entry => entry.role === role) : undefined;

/** Each descriptor from Sanity's role-keyed lists, or from the flat name the Sitecore adapter gives it. */
const readFormFields = (content: HeatDamageFormContent): HeatDamageFormFields =>
  Object.fromEntries(
    FIELD_ROLES.map(role => [
      role,
      findByRole(content.formFields, role) ?? findByRole(content.radioGroups, role) ?? content[role] ?? {},
    ]),
  ) as unknown as HeatDamageFormFields;

/**
 * `HeatDamageForm` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/forms/src/HeatDamageForm/HeatDamageForm.tsx` 1:1: the damage
 * questions, a file upload, the contact details and reCAPTCHA, posting to the forms v2 endpoint with the
 * files as base64, then redirecting to the send button's link; a failure shows the file or generic
 * error notification. Drops the legacy `wrap()`/Experience Editor `editable` checks — this lane never
 * runs inside the Experience Editor.
 */
const HeatDamageForm: FC = () => {
  const { adapter } = useCms();
  const content = useContent<HeatDamageFormContent>();
  const formFields = readFormFields(content);
  const { uploadFilesFormField } = formFields;
  const sendButtonLink = linkWithUrl(content.sendButtonLink);
  const senderId = content.senderIdOption ?? '';

  const { pushHref } = useRouter();
  const recaptchaRef = useRef<RecaptchaRef>(null);
  const { send, isSuccess, isError, error: postError } = useFormsPublicV2PostFormV2();
  const { label, businessUnit } = useDC();

  const [errorType, setErrorType] = useState<FormErrorType | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [hasFileError, setHasFileError] = useState<boolean>(false);

  const resolver = typedYupResolver<FormValues>(getSchema(formFields));
  const methods = useForm<FormValues>({
    mode: 'onBlur',
    resolver,
  });
  const {
    control,
    register,
    formState: { errors },
  } = methods;

  const submitForm: SubmitHandler<FormValues> = async values => {
    setIsSubmitting(true);

    if (!hasFileError) {
      try {
        const attachments = files
          ? await Promise.all(
              files.map(async file => {
                const fileContent = await fileToBase64(file);
                return {
                  filename: file.name,
                  content: fileContent,
                };
              }),
            )
          : undefined;

        const requestBody: RequestModels_Forms_V2_FormsRequestModelV2 = {
          data: {
            destinationCode: senderId,
            attachments: attachments,
            city: values.city,
            emailAddress: values.email,
            formFields: [
              {
                key: 'Woningeigenaar of huurder',
                value: values.isHomeOwner ? 'Woningeigenaar' : 'huurder',
              },
              {
                key: 'Omschrijving schade',
                value: values.description,
              },
              {
                key: 'Naam verzekeraar',
                value: values.insurer,
              },
              {
                key: 'Inschatting schadebedrag',
                value: values.estimatedDamage,
              },
              {
                key: 'Radio Button List - Aanhef',
                value: values.salutation,
              },
              {
                key: 'Telefoonnummer',
                value: values.phoneNumber,
              },
              {
                key: 'Client Number - Klantnummer',
                value: values.customerId.toString(),
              },
              {
                key: 'Multi-line text - Vraag of opmerking',
                value: values.remarks,
              },
              { key: 'Voornaam', value: values.firstname },
            ],
            houseNumber: +values.houseNumber,
            postalCode: values.postalCode,
            street: values.street,
            surnamePreposition: values.surnamePreposition,
            surname: values.surname,
            urlOrigin: window.location.href,
          },
        };
        await send({
          label,
          formId: stripAccolades(senderId),
          businessUnit,
          requestBody,
          recaptchaEnterpriseToken: values.recaptchaToken,
        } as Parameters<typeof send>[0]);
      } catch (e) {
        logger.error('aVrkE9', 'Failed to submit form', { error: e });
        setErrorType(generateErrorType(e));
      }
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!isSuccess) return;
    setErrorType(undefined);
    pushHref(toURL(sendButtonLink?.href ?? ''));
  }, [isSuccess, sendButtonLink?.href, pushHref]);

  useEffect(() => {
    if (!isError) return;
    setErrorType(generateErrorType(postError));
    logger.error('LpYJ9d', 'Failed to submit form', { postError });
  }, [isError, postError]);

  const notification: HeatDamageNotification | undefined = errorType
    ? (findByRole(content.notifications, `${errorType}ErrorNotification`) ??
      content[`${errorType}ErrorNotification`])
    : undefined;

  return (
    <FormProvider {...methods}>
      <RecaptchaForm onSubmit={submitForm} recaptchaRef={recaptchaRef}>
        <Stack gap="6">
          <Heading as="h2" size="M">
            {content.formTitle}
          </Heading>
          <DamageInputs formFields={formFields} register={register} errors={errors} control={control} />
          <Heading as="h3" size="S">
            {uploadFilesFormField.label}
          </Heading>
          <Stack.Item>
            <RichText html={adapter.toRichText(uploadFilesFormField.hint)} />
          </Stack.Item>
          <FilePicker
            files={files}
            setFiles={setFiles}
            name="attachments"
            hasError={hasFileError}
            setHasError={setHasFileError}
            accept={['JPG', 'JPEG', 'PNG', 'PDF']}
          />
          <ContactInputs
            title={content.contactDetailsTitle}
            formFields={formFields}
            register={register}
            errors={errors}
            control={control}
          />
          <Text size="BodyS">
            <Recaptcha hasError={!!errors.recaptchaToken} ref={recaptchaRef} />
          </Text>
          {errorType ? (
            <NotificationBox
              isAlert
              text={<RichText html={adapter.toRichText(notification?.content)} />}
              title={notification?.title}
              variant={notification?.variant}
            />
          ) : null}
          <Stack.Item grow={false}>
            <Button type="submit" isLoading={isSubmitting}>
              {content.sendButtonLink?.text}
            </Button>
          </Stack.Item>
        </Stack>
      </RecaptchaForm>
    </FormProvider>
  );
};

export default HeatDamageForm;
